import { List, ListItem, ListItemIcon } from "@mui/material";
import { IoTrash } from "react-icons/io5";
import { colors } from "../../../styles/variables";
import { Feature, VoiceMessage } from "../../../types/api";
import { sortVoiceMessages } from "../../../utils/voice-message-utils";
import { VoiceMessageLIText } from "../VoiceMessageLIText";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";
import { useState } from "react";

export interface VoiceMessageListProps {
  voiceMessages: VoiceMessage[];
  showIcon?: boolean;
  onListIconClick?: (activity: VoiceMessage) => void;
  isDragDisabled: boolean;
  setPlaylistActivities?: React.Dispatch<React.SetStateAction<VoiceMessage[]>>;
}

const reorder = (
  list: VoiceMessage[],
  startIndex: number,
  endIndex: number
): VoiceMessage[] => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

export const VoiceMessageList: React.FC<VoiceMessageListProps> = ({
  voiceMessages,
  showIcon,
  onListIconClick,
  isDragDisabled,
  setPlaylistActivities,
}) => {
  const [cloneActivity, setCloneActivity] = useState<VoiceMessage>(
    voiceMessages[0]
  );

  const onDragEnd = (result: DropResult): void => {
    if (!result.destination) {
      return;
    }
    if (result.destination.index === result.source.index) {
      return;
    }

    const activities: VoiceMessage[] = reorder(
      voiceMessages,
      result.source.index,
      result.destination.index
    );
    
    setPlaylistActivities?.(activities);
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable
        droppableId="droppable"
        renderClone={(provided): JSX.Element => (
          <div
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            ref={provided.innerRef}
          >
            <ListItem
              sx={{
                backgroundColor:
                  cloneActivity?.feature !== Feature.Instructions
                    ? colors.paleBlue
                    : "white",
              }}
            >
              <VoiceMessageLIText voiceMessage={cloneActivity} />
            </ListItem>
          </div>
        )}
      >
        {(provided): JSX.Element => (
          <List ref={provided.innerRef} {...provided.droppableProps}>
            {sortVoiceMessages(voiceMessages ?? []).map(
              (activity: VoiceMessage, index) => (
                <Draggable
                  key={activity.id}
                  draggableId={activity.id.toString()}
                  index={index}
                  isDragDisabled={isDragDisabled}
                >
                  {(provided): JSX.Element => (
                    <ListItem
                      key={activity?.id}
                      sx={{
                        backgroundColor:
                          activity?.feature !== Feature.Instructions
                            ? colors.paleBlue
                            : "white",
                        "&:hover": {
                          color: `${colors.darkBlue}`,
                        },
                      }}
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      divider
                      onMouseDown={() => setCloneActivity(activity)}
                    >
                      <VoiceMessageLIText voiceMessage={activity} />
                      {showIcon && (
                        <ListItemIcon
                          onClick={() =>
                            onListIconClick ? onListIconClick(activity) : null
                          }
                          sx={[
                            {
                              "&:hover": {
                                cursor: "pointer",
                              },
                            },
                          ]}
                        >
                          <IoTrash />
                        </ListItemIcon>
                      )}
                    </ListItem>
                  )}
                </Draggable>
              )
            )}
            {provided.placeholder}
          </List>
        )}
      </Droppable>
    </DragDropContext>
  );
};
